export { CASH_DIFFERENCES_PAGE_SIZE } from './cashDifferencesConstants';

import { supabase } from '../../shared/supabase/supabaseClient';
import {
  type CashSessionsHistoryFilters,
} from './cashHistoryQuery';
import {
  buildCashSessionPaymentBreakdown,
  type CashPaymentMethodLine,
} from './cashSummaryBreakdown';
import type { CashSessionRow } from './cashSessionRow';
export type { CashSummary } from './cashSummaryTypes';
import type { CashSummary } from './cashSummaryTypes';

export async function getCashSummary(_branchId: string): Promise<CashSummary> {
  return {
    activeSession: null,
    todaySalesCount: 0,
    todaySalesTotal: 0,
    todayCashTotal: 0,
    sessionCashTotal: 0,
    sessionSalesCount: 0,
    sessionSalesTotal: 0,
    sessionTipsTotal: 0,
    sessionPaymentBreakdown: [],
    sessionOutflowsTotal: 0,
    sessionInflowsTotal: 0,
    todayDigitalTotal: 0,
    lastClosedSession: null,
    expectedBalance: null,
  };
}

/** Historial de cierres de caja (multi-turno) con filtros y paginación */
export async function getCashSessionsHistory(
  _branchId: string,
  _opts?: CashSessionsHistoryFilters
): Promise<CashSessionRow[]> {
  return [];
}

/** Total de cierres en rango con filtros (paginación). */
export async function countCashSessionsHistory(
  _branchId: string,
  _opts?: Omit<CashSessionsHistoryFilters, 'limit' | 'offset' | 'sort'>
): Promise<number> {
  return 0;
}

export type CashDifferenceRow = {
  id: string;
  closedAt: string;
  expectedAmount: number;
  closingAmount: number;
  difference: number;
  note?: string | null;
};

export type GetCashDifferencesSummaryOpts = {
  lastDays?: number;
  limit?: number;
  offset?: number;
};

export async function getCashDifferencesSummary(
  _branchId: string,
  _lastDaysOrOpts?: number | GetCashDifferencesSummaryOpts
): Promise<CashDifferenceRow[]> {
  return [];
}

export type { CashSessionsHistoryFilters } from './cashHistoryQuery';
export { CASH_SESSION_PAYMENT_METHODS, buildCashSessionPaymentBreakdown } from './cashSummaryBreakdown';
export type { CashPaymentMethodLine } from './cashSummaryBreakdown';

export type CashSessionDetail = {
  salesCount: number;
  salesTotal: number;
  /** Egresos activos del turno (direction='out'). */
  outflowsTotal: number;
  /** Ingresos manuales activos del turno (direction='in'). */
  inflowsTotal: number;
  paymentBreakdown: CashPaymentMethodLine[];
};

export async function getCashSessionDetail(_session: {
  id: string;
  openedAt: string;
}): Promise<CashSessionDetail> {
  return {
    salesCount: 0,
    salesTotal: 0,
    outflowsTotal: 0,
    inflowsTotal: 0,
    paymentBreakdown: [],
  };
}

/**
 * Fetch remoto del detalle de una sesion via RPC `reports_cash_session_detail`.
 * Retorna null si hay error de red o RPC (caller debe hacer fallback local).
 * P1 #851: localDb stub retorna zeros — este fetch es el path principal en produccion.
 */
export async function fetchRemoteCashSessionDetail(sessionId: string): Promise<CashSessionDetail | null> {
  const { data, error } = await supabase.rpc('reports_cash_session_detail', {
    p_session_id: sessionId,
  });
  if (error) {
    console.warn('[cashSummaryQuery] reports_cash_session_detail', error.message);
    return null;
  }
  const j = data as Record<string, unknown> | null;
  if (!j) return null;
  const breakdownRaw = j.payment_breakdown;
  return {
    salesCount: Number(j.sales_count ?? 0),
    salesTotal: Number(j.sales_total ?? 0),
    outflowsTotal: Number(j.outflows_total ?? 0),
    inflowsTotal: Number(j.inflows_total ?? 0),
    paymentBreakdown: buildCashSessionPaymentBreakdown(
      Array.isArray(breakdownRaw)
        ? (breakdownRaw as { method_id?: string; total?: number }[])
        : []
    ),
  };
}

/**
 * Obtiene el detalle de una sesion, prefiriendo remoto (P1 #851).
 * En online: usa RPC reports_cash_session_detail.
 * En offline/error: fallback a getCashSessionDetail (retorna zeros).
 */
export async function getCashSessionDetailRemoteFirst(session: {
  id: string;
  openedAt: string;
}): Promise<CashSessionDetail> {
  const remote = await fetchRemoteCashSessionDetail(session.id);
  if (remote != null) return remote;
  return getCashSessionDetail(session);
}
